/**
 * Delivery Settings Card (MVP)
 *
 * Delivery speed (tier) + caps only. Contract-based model.
 */

import { useMemo } from "react";
import { Calendar, Hash, ToggleLeft, ToggleRight, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { VelocityTierSelector } from "./VelocityTierSelector";
import {
  type VelocityTier,
  bidToVelocityTier,
  velocityTierToBid,
} from "@/lib/velocityTier";
import type { LeadPreference } from "@/contexts/AgentContext";
import type { PricingRule } from "@/contexts/PricingRulesContext";

interface DeliverySettingsCardProps {
  preference: LeadPreference;
  pricingRules: PricingRule[];
  onToggleActive: () => void;
  onUpdateGlobalBid: (value: number) => void;
  onUpdateDailyCap: (value: number) => void;
  onUpdateMonthlyCap: (value: number) => void;
}

const DeliverySettingsCard = ({
  preference,
  pricingRules,
  onToggleActive,
  onUpdateGlobalBid,
  onUpdateDailyCap,
  onUpdateMonthlyCap,
}: DeliverySettingsCardProps) => {
  const baselineMinPrice = useMemo(() => {
    const allRule = pricingRules.find((r) => r.state === "ALL" && r.active);
    if (allRule) return allRule.basePrice;
    const activeRules = pricingRules.filter((r) => r.active);
    if (activeRules.length === 0) return 20;
    return Math.min(...activeRules.map((r) => r.basePrice));
  }, [pricingRules]);

  const currentTier = bidToVelocityTier(preference.bidValue, baselineMinPrice);

  const handleTierChange = (tier: VelocityTier) => {
    const newBid = velocityTierToBid(tier, baselineMinPrice);
    onUpdateGlobalBid(newBid);
  };

  return (
    <div
      className={cn(
        "rounded-xl border transition-all",
        preference.isActive
          ? "bg-card border-border"
          : "bg-muted/30 border-border/50"
      )}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <button
            onClick={onToggleActive}
            className="flex-shrink-0 mt-0.5"
            aria-label={preference.isActive ? "Pause delivery" : "Resume delivery"}
          >
            {preference.isActive ? (
              <ToggleRight className="w-7 h-7 text-accent" />
            ) : (
              <ToggleLeft className="w-7 h-7 text-muted-foreground" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={cn(
                "font-semibold",
                preference.isActive ? "text-foreground" : "text-muted-foreground"
              )}>
                {preference.leadTypeName}
              </h3>

              {!preference.isActive && (
                <Badge variant="secondary" className="text-xs">
                  Paused
                </Badge>
              )}
            </div>

            {preference.isActive && (
              <div className="mt-4">
                <label className="text-xs text-muted-foreground mb-2 block">
                  Delivery Speed
                </label>
                <VelocityTierSelector
                  value={currentTier}
                  onChange={handleTierChange}
                  disabled={!preference.isActive}
                />
                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Speed affects pacing only — not total volume.
                </div>
              </div>
            )}

            {preference.isActive && (
              <div className="mt-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[100px]">
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Daily Limit
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min={0}
                      value={preference.dailyCap || ""}
                      onChange={(e) => onUpdateDailyCap(Number(e.target.value))}
                      className="pl-8 h-9 w-20"
                      disabled={!preference.isActive}
                    />
                  </div>
                </div>

                <div className="min-w-[100px]">
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Monthly Limit
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min={0}
                      value={preference.monthlyCap || ""}
                      onChange={(e) => onUpdateMonthlyCap(Number(e.target.value))}
                      className="pl-8 h-9 w-20"
                      disabled={!preference.isActive}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliverySettingsCard;
